import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Bot } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface OpenAIModel {
  id: string;
  codigo: string;
  nombre: string;
  created_at: string;
}

export default function OpenAIModels() {
  const [models, setModels] = useState<OpenAIModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState<OpenAIModel | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newModel, setNewModel] = useState({
    codigo: '',
    nombre: ''
  });

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('openai_models')
        .select('*')
        .order('codigo');

      if (error) throw error;
      setModels(data || []);
    } catch (error: any) {
      console.error('Error fetching models:', error);
      setError('Error al cargar los modelos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingModel) {
        const { error } = await supabase
          .from('openai_models')
          .update({
            codigo: newModel.codigo,
            nombre: newModel.nombre
          })
          .eq('id', editingModel.id);

        if (error) throw error;
        setSuccess('Modelo actualizado correctamente');
      } else {
        const { error } = await supabase
          .from('openai_models')
          .insert([newModel]);

        if (error) throw error;
        setSuccess('Modelo creado correctamente');
      }

      setShowModal(false);
      setEditingModel(null);
      setNewModel({ codigo: '', nombre: '' });
      fetchModels();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleEdit = (model: OpenAIModel) => {
    setEditingModel(model);
    setNewModel({
      codigo: model.codigo,
      nombre: model.nombre
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este modelo?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('openai_models')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('Modelo eliminado correctamente');
      fetchModels();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const filteredModels = models.filter(model =>
    model.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Modelos de OpenAI</h1>
        <button 
          onClick={() => {
            setEditingModel(null);
            setNewModel({ codigo: '', nombre: '' });
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Modelo
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar modelos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-semibold text-gray-600">Código</th>
                  <th className="pb-3 font-semibold text-gray-600">Nombre</th>
                  <th className="pb-3 font-semibold text-gray-600">Creado</th>
                  <th className="pb-3 font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredModels.map((model) => (
                  <tr key={model.id} className="border-b">
                    <td className="py-3">
                      <div className="flex items-center">
                        <Bot className="w-5 h-5 text-blue-500 mr-2" />
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {model.codigo}
                        </code>
                      </div>
                    </td>
                    <td className="py-3">{model.nombre}</td>
                    <td className="py-3">
                      {new Date(model.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(model)}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                          title="Editar modelo"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(model.id)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                          title="Eliminar modelo"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredModels.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-gray-500">
                      <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No se encontraron modelos</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal para añadir/editar modelo */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingModel ? 'Editar Modelo' : 'Nuevo Modelo'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código del Modelo
                </label>
                <input
                  type="text"
                  required
                  value={newModel.codigo}
                  onChange={(e) => setNewModel({...newModel, codigo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="gpt-4"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  required
                  value={newModel.nombre}
                  onChange={(e) => setNewModel({...newModel, nombre: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="GPT-4"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingModel(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingModel ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}